import FavoriteIcon from "@mui/icons-material/Favorite";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import GroupIcon from "@mui/icons-material/Group";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LoopIcon from "@mui/icons-material/Loop";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import page4_section1 from "../../../assets/images/AboutVedaz/page4_section1.jpg";
import page4_section2 from "../../../assets/images/AboutVedaz/page4_section2.png";
import classes from "../AboutVedaz.styles";

interface Props {
  chapterIndex: number;
}

const iconBoxStyle = {
  width: 40,
  height: 40,
  borderRadius: "12px",
  background: "rgba(144,83,246,0.15)",
  border: "1px solid rgba(144,83,246,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "#9053F6",
};

const ForWhom = ({ chapterIndex }: Props) => {
  const { t } = useTranslation();

  const audience: { title: string; body: string; Icon: React.ElementType }[] = [
    {
      title: t("about_whom_1_title"),
      body: t("about_whom_1_body"),
      Icon: FavoriteIcon,
    },
    {
      title: t("about_whom_2_title"),
      body: t("about_whom_2_body"),
      Icon: LoopIcon,
    },
    {
      title: t("about_whom_3_title"),
      body: t("about_whom_3_body"),
      Icon: LightbulbIcon,
    },
    {
      title: t("about_whom_4_title"),
      body: t("about_whom_4_body"),
      Icon: GroupIcon,
    },
    {
      title: t("about_whom_5_title"),
      body: t("about_whom_5_body"),
      Icon: TrendingUpIcon,
    },
    {
      title: t("about_whom_6_title"),
      body: t("about_whom_6_body"),
      Icon: FlightTakeoffIcon,
    },
  ];

  const leftFeatures = [audience[0], audience[2], audience[4]];
  const rightFeatures = [audience[1], audience[3], audience[5]];

  return (
    <Box>
      {/* Section 1: Background image with glass content card */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page4_section1}
          alt=""
          sx={classes.bgSectionMedia}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <Typography
            sx={{
              ...classes.sectionHeading,
              background:
                "linear-gradient(128deg, #FFFFFF 0%, #EDE9FE 28%, #A78BFA 52%, #6366F1 72%, #22D3EE 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("about_whom_heading")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "380px", mb: 0 }}>
            {t("about_whom_s1_body")}
          </Typography>
        </Box>
      </Box>

      {/* Section 2: Center image with 6 features around it */}
      <Box sx={classes.plainSection}>
        <Typography
          sx={{ ...classes.sectionHeading, textAlign: "center", mb: 4 }}
        >
          {t("about_whom_s2_heading")}
        </Typography>

        <Grid container spacing={3} alignItems="center">
          {/* Left column: features 1, 3, 5 */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 1 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {leftFeatures.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    ...classes.featureCard,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Box sx={iconBoxStyle}>
                    <item.Icon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={classes.featureTitle}>
                      {item.title}
                    </Typography>
                    <Typography sx={classes.featureBody}>
                      {item.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Center: phone mockup image */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              order: { xs: 1, md: 2 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: "220px", sm: "260px", md: "100%" },
                mx: "auto",
                "& img": {
                  width: "100%",
                  display: "block",
                  filter: "drop-shadow(0 0 40px rgba(144,83,246,0.35))",
                },
              }}
            >
              <img src={page4_section2} alt={t("about_whom_s2_heading")} />
            </Box>
          </Grid>

          {/* Right column: features 2, 4, 6 */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 3, md: 3 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {rightFeatures.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    ...classes.featureCard,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Box sx={iconBoxStyle}>
                    <item.Icon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={classes.featureTitle}>
                      {item.title}
                    </Typography>
                    <Typography sx={classes.featureBody}>
                      {item.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ForWhom;
